<template>
  <div class="pages">
    <div class="hd_box">
      <h3>站点配置</h3>
      <p class="desc">配置用于整站信息配置</p>
    </div>

    <div class="box_container">
      <el-tabs v-model="activeLang" class="demo-tabs" @tab-change="handleTabChange">
        <el-tab-pane 
          v-for="lang in languages" 
          :key="lang.code" 
          :label="lang.label" 
          :name="lang.code"
        >
          <el-form :model="forms[lang.code]" label-width="auto" style="max-width: 1080px">
            <el-form-item :label="getLabel('name', lang.code)">
              <el-input 
                v-model="forms[lang.code].name" 
                :placeholder="getPlaceholder('name', lang.code)" 
              />
            </el-form-item>
            <el-form-item :label="getLabel('keywords', lang.code)">
              <el-input 
                v-model="forms[lang.code].keywords" 
                :placeholder="getPlaceholder('keywords', lang.code)" 
              />
            </el-form-item>
            <el-form-item :label="getLabel('description', lang.code)">
              <el-input 
                v-model="forms[lang.code].description" 
                type="textarea" 
                :placeholder="getPlaceholder('description', lang.code)" 
              />
            </el-form-item>
            <el-form-item :label="getLabel('email', lang.code)">
              <el-input 
                v-model="forms[lang.code].email" 
                :placeholder="getPlaceholder('email', lang.code)" 
              />
            </el-form-item>
            <el-form-item :label="getLabel('address', lang.code)">
              <el-input 
                v-model="forms[lang.code].address" 
                type="textarea" 
                :placeholder="getPlaceholder('address', lang.code)" 
              />
            </el-form-item>
            <el-form-item :label="getLabel('address1', lang.code)">
              <el-input 
                v-model="forms[lang.code].address1" 
                type="textarea" 
                :placeholder="getPlaceholder('address1', lang.code)" 
              />
            </el-form-item>
            <el-form-item :label="getLabel('recordNumber', lang.code)">
              <el-input 
                v-model="forms[lang.code].recordNumber" 
                :placeholder="getPlaceholder('recordNumber', lang.code)" 
              />
            </el-form-item>
            <el-form-item :label="getLabel('copyright', lang.code)">
              <el-input 
                v-model="forms[lang.code].copyright" 
                :placeholder="getPlaceholder('copyright', lang.code)" 
              />
            </el-form-item>
            <el-form-item :label="getLabel('wechatQr', lang.code)">
              <el-upload 
                class="avatar-uploader" 
                :show-file-list="false" 
                :http-request="(option) => handleUpload(option, lang.code, 'wechatQr')"
                name="image"
              >
                <img 
                  :src="forms[lang.code].wechatQr" 
                  v-if="forms[lang.code].wechatQr" 
                  class="avatar" 
                />
                <el-icon v-else class="avatar-uploader-icon" :size="60">
                  <Picture />
                </el-icon>
              </el-upload>
            </el-form-item>
            <el-form-item :label="getLabel('ownQR', lang.code)">
              <el-upload 
                class="avatar-uploader" 
                :show-file-list="false" 
                :http-request="(option) => handleUpload(option, lang.code, 'ownQR')"
                name="image"
              >
                <img 
                  :src="forms[lang.code].ownQR" 
                  v-if="forms[lang.code].ownQR" 
                  class="avatar" 
                />
                <el-icon v-else class="avatar-uploader-icon" :size="60">
                  <Picture />
                </el-icon>
              </el-upload>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
      
      <div class="flex">
        <el-button type="primary" @click="onSubmit">提交</el-button>
        <el-button @click="getConfig">刷新</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { ElMessage } from "element-plus";
import { siteConfig, editConfig } from '@/api/user.js'
import { uploadImg } from '@/api/article.js'

// 语言配置
const languages = [
  { code: 'zh-CN', label: '中文配置' },
  { code: 'en', label: '英文配置' }
  // 以后新增语言只需在这里添加即可
]

const activeLang = ref('zh-CN')

// 表单数据 - 使用对象存储所有语言的表单
const forms = reactive({})

// 初始化所有语言的表单
languages.forEach(lang => {
  forms[lang.code] = {
    name: '',
    keywords: '',
    description: '',
    email: '',
    recordNumber: "",
    copyright: "",
    address: '',
    wechatQr: '',
    ownQR: '',
    address1: ''
  }
})

// 标签映射
const labels = {
  name: {
    'zh-CN': 'SEO站点名称',
    en: 'SEO Site Name'
  },
  keywords: {
    'zh-CN': 'SEO关键字',
    en: 'SEO Keywords'
  },
  description: {
    'zh-CN': 'SEO描述',
    en: 'SEO Description'
  },
  email: {
    'zh-CN': '底部邮箱',
    en: 'Footer Email'
  },
  address: {
    'zh-CN': '底部地址',
    en: 'Footer Address'
  },
  address1: {
    'zh-CN': '底部地址2',
    en: 'Footer Address 2'
  },
  recordNumber: {
    'zh-CN': '网站备案号',
    en: 'Record Number'
  },
  copyright: {
    'zh-CN': '版权',
    en: 'Copyright'
  },
  wechatQr: {
    'zh-CN': '微信二维码',
    en: 'WeChat QR Code'
  },
  ownQR: {
    'zh-CN': '联系我们二维码',
    en: 'Contact Us QR Code'
  }
}

// 占位符映射
const placeholders = {
  name: {
    'zh-CN': '请输入您的站点名称',
    en: 'Please enter your site name'
  },
  keywords: {
    'zh-CN': '请输入您的关键字',
    en: 'Please enter your keywords'
  },
  description: {
    'zh-CN': '请输入您的网站描述',
    en: 'Please enter your site description'
  },
  email: {
    'zh-CN': '请输入您的邮箱',
    en: 'Please enter your email'
  },
  address: {
    'zh-CN': '请输入您的地址',
    en: 'Please enter your address'
  },
  address1: {
    'zh-CN': '请输入您的地址',
    en: 'Please enter your address'
  },
  recordNumber: {
    'zh-CN': '请输入您的网站备案号',
    en: 'Please enter your record number'
  },
  copyright: {
    'zh-CN': '请输入您的版权',
    en: 'Please enter your copyright'
  }
}

// 获取标签文本
const getLabel = (field, lang) => {
  return labels[field]['zh-CN'] || field;
}

// 获取占位符文本
const getPlaceholder = (field, lang) => {
  return placeholders[field]['zh-CN'] || ''
}

const onSubmit = () => {
  const currentForm = forms[activeLang.value]
  const obj = { 
    body: JSON.stringify(currentForm),
  }
  
  editConfig(obj, activeLang.value)
    .then(res => {
      ElMessage.success('设置成功')
      getConfig()
    })
    .catch(err => {
      ElMessage.error(err.message)
    })
}

const getConfig = () => {
  siteConfig({locale: activeLang.value})
    .then(res => {
      const data = JSON.parse(res.body)
      console.log(data);
      // 处理各语言数据
        for (const key in data) {
            forms[activeLang.value][key] = data[key]
        }
    })
    .catch(err => {
      console.log(err)
    })
}

getConfig()

// Tab切换处理
const handleTabChange = async(tab) => {
  activeLang.value = tab
  await getConfig()
}

// 统一处理图片上传
const handleUpload = (option, langCode, fieldType) => {
  const data = new FormData()
  data.append('image', option.file)
  uploadImg(data)
    .then(res => {
      ElMessage.success('上传成功')
      forms[langCode][fieldType] = res.url
    })
    .catch(err => {
      ElMessage.error('上传失败')
    })
}
</script>

<style lang="scss" scoped>
.pages {
  width: 100%;

  .hd_box {
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 0 10px #eee;
    padding: 10px 30px;
  }

  h3 {
    margin-bottom: 20px;
  }

  .desc {
    color: #aaa;
    font-size: 14px;
  }

  .box_container {
    margin-top: 30px;
    background-color: white;
    border-radius: 10px;
    box-shadow: #eee;
    padding: 10px 30px;

    .flex {
      display: flex;
      align-items: center;
      width: 200px;
      justify-content: space-between;
      margin: auto;
    }
  }
}

.avatar {
  width: 160px;
  height: 160px;
}
</style>